import React from "react";
import { Button } from "../../atoms/button/button";
import {
  TEXT_LEVELS,
  Text,
  headingRank,
  plainText,
  safeElement,
  type TextLevel
} from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import { isSafeImageSrc } from "../../services/safe-image-src";
import { Tooltip } from "../../atoms/tooltip/tooltip.client";
import { Disclosure } from "./disclosure";
import { isRenderable } from "./is-renderable.shared";
import { PackCollapse } from "./pack-collapse.client";
import { packProvablyFits } from "./pack-collapse.shared";
import { ShowMoreToggle } from "./show-more-toggle";
import { localityKindFor, type LocalityKind } from "./locality-badge";
import { LogoGrid, type LogoGridItem } from "./logo-grid";
import type { LineupChannel, LineupPack } from "./channel-lineup.types";
import styles from "./genre-pack.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One Genre Pack card (GP1-GP9, GP12-GP14, GP17): its title and ⓘ, its Shop button, its
 * Subscriptions and Channels logo grids, and the three things a single pack can carry that no other
 * pack does — a ZIP control (GP13), a footer link that opens a pop-up (GP9) and a wordmark in place
 * of its name (GP14).
 */
export interface GenrePackProps {
  pack: LineupPack;
  /** AX5 — pack titles are real headings, ranked after the page's own h1. */
  titleLevel?: TextLevel;
  /** GP5, content-managed and shared by every pack. Shows only when written. */
  subscriptionsHeading?: string;
  /** GP6, content-managed and shared by every pack. Shows only when written. */
  channelsHeading?: string;
  /** GP12 — the collapsed toggle's wording. */
  showMoreLabel?: string;
  /** GP12 — the expanded toggle's wording. */
  showLessLabel?: string;
  /** GP7 — badge wording by kind; a kind with none draws no badge. */
  badgeLabels?: Partial<Record<LocalityKind, string>>;
  /** GP7 — badge ⓘ text by kind; a kind with none draws no ⓘ. */
  badgeTooltips?: Partial<Record<LocalityKind, string>>;
  /**
   * GP13 — "the MyHome Team pack has its own ZIP control". Built elsewhere (`zip-line.tsx` plus its
   * `controlSlot`) and placed under this pack's title; absent on every other pack.
   */
  zipSlot?: React.ReactNode;
  /**
   * GP9 — "the MyHome Team pack shows a content-managed footer link … that opens the MyHome Team
   * team list in a pop-up". The page supplies that link (an iframe-modal `Link`); this places it
   * last in the card.
   */
  footerSlot?: React.ReactNode;
  /** Layout only. */
  className?: string;
}

/* A map, not `styles[\`cta-${variant}\`]`: a renamed class would read `undefined` and show both CTAs. */
const CTA_TIER_CLASS: Record<"tonal" | "secondary", string> = {
  tonal: styles.ctaTonal,
  secondary: styles.ctaSecondary
};

/** GP2's suffix rule. */
function packTitleSuffix(pack: LineupPack): string {
  if (pack.genreType === "MINI") {
    return " | Mini Pack";
  }
  if (pack.kind === "addon") {
    return " | Add-On";
  }
  return "";
}

/**
 * ZP8 / GP17 — "an out-of-market RSN with the same name as an RSN in the user's market shows once,
 * as the market RSN". Narrowed to the RSN pair on purpose, and a general name-key would be a
 * defect.
 */
export function dedupeMarketRsns(channels: LineupChannel[]): LineupChannel[] {
  const marketRsnNames = new Set<string>();
  for (const channel of channels) {
    if (channel.kind === "rsn") {
      marketRsnNames.add(channel.name.trim().toLowerCase());
    }
  }
  if (marketRsnNames.size === 0) {
    return channels;
  }
  return channels.filter(
    channel => channel.kind !== "rsn-oom" || !marketRsnNames.has(channel.name.trim().toLowerCase())
  );
}

/** Own-property lookup, not a bare index: these records arrive from CMS, and
 *  a bare index answers `"constructor"` with a function. */
function authored(
  wording: Partial<Record<LocalityKind, string>> | undefined,
  kind: LocalityKind
): string | undefined {
  return wording && Object.prototype.hasOwnProperty.call(wording, kind) ?
    wording[kind] :
    undefined;
}

function GenrePackImpl({
  pack,
  titleLevel = "h2",
  subscriptionsHeading,
  channelsHeading,
  showMoreLabel,
  showLessLabel,
  badgeLabels,
  badgeTooltips,
  zipSlot,
  footerSlot,
  className
}: GenrePackProps) {
  const level = safeElement(titleLevel, "h2", TEXT_LEVELS);
  // GP5/GP6's own headings rank one level under the pack title, whatever level
  // the caller asked for — never a hardcoded h4.
  const subRank = Math.min((headingRank(level) ?? 2) + 1, 6);
  const subLevel = `h${subRank}` as TextLevel;

  // GP1 — "pack names show exactly as the content team enters them"; GR9's
  // markup rule is applied defensively, same as every other authored label in
  // this tree.
  /* The suffix is kept out of `baseTitle`, and that is load-bearing for the wordmark gate below. */
  const baseTitle = plainText(pack.title);
  const titleSuffix = packTitleSuffix(pack);
  const title = baseTitle + titleSuffix;

  // GP4 — both a label and a link are required, or nothing renders.
  /* The href is trimmed, exactly as the label beside it is — though no longer for the gate. */
  const ctaLabel = (pack.ctaLabel ?? "").trim();
  const ctaHref = (pack.ctaHref ?? "").trim();
  const hasCta = Boolean(ctaLabel) && isSafeHref(ctaHref);

  // GP2 — "preceded by the pack icon if it has one".
  const askedIcon = (pack.iconUrl ?? "").trim();
  const iconUrl = isSafeImageSrc(askedIcon) ? askedIcon : "";

  /* GP14 — "it has the MyFree logo as its title". The wordmark replaces the visible wording and
     carries it as `alt`, so the heading keeps the same accessible name either way. */
  const askedTitleImage = (pack.titleImageUrl ?? "").trim();
  const titleImageUrl =
    baseTitle !== "" && isSafeImageSrc(askedTitleImage) ? askedTitleImage : "";

  if (process.env.NODE_ENV !== "production" && baseTitle === "" && askedTitleImage !== "") {
    console.warn(
      "[GenrePack] a wordmark (`titleImageUrl`) was given with no `title`, so it " +
        "was not rendered — `title` is the image's only `alt` and the pack's only " +
        "accessible name. The text title is shown instead."
    );
  }

  /* The MyFree pack is the one `genreAddon` pack (`resolve-lineup-for.ts`); its header and discs have
     their own frames (`16545:20746`, `16545:21285`). */
  const myFreePack = pack.kind === "genreAddon";

  // GP14 — the content-managed subtitle. Blank draws nothing.
  const subtitle = plainText((pack.subtitle ?? "").trim());

  /* GR9 — "channel names, labels and team names always show as plain text". These two are
     author-typed scalars like every other label here, and `logo-grid.tsx` renders its heading as
     given; reducing at this boundary keeps that leaf free of the parser. */
  const subsHeading = subscriptionsHeading === undefined ?
    undefined :
    plainText(subscriptionsHeading);
  const chansHeading = channelsHeading === undefined ? undefined : plainText(channelsHeading);

  const subscriptionItems: LogoGridItem[] = pack.subscriptions.map(subscription => ({
    id: subscription.id,
    logoUrl: subscription.logoUrl,
    name: subscription.name
  }));

  /* GP17 — the MyHome Team pack's two kinds of RSN reach this component in one `channels` array;
     ZP8 decides which of a duplicated pair survives. GP8 is not code here, and that is the point. */
  const channelItems: LogoGridItem[] = dedupeMarketRsns(pack.channels).map(channel => {
    // GP7 / ZP9 — one decision, taken in locality-badge.tsx and nowhere else.
    const badgeKind = localityKindFor(channel) ?? undefined;
    return {
      id: channel.id,
      logoUrl: channel.logoUrl,
      name: channel.name,
      badgeKind,
      badgeLabel: badgeKind ? authored(badgeLabels, badgeKind) : undefined,
      badgeTooltip: badgeKind ? authored(badgeTooltips, badgeKind) : undefined
    };
  });

  return (
    <article className={[styles.pack, className].filter(Boolean).join(" ")} data-cl-pack={myFreePack ? "myfree" : undefined}>
      <div className={styles.titleRow}>
        {iconUrl !== "" && titleImageUrl === "" && (
          // `alt=""` is load-bearing, not an oversight — see the header.
          <img
            alt=""
            className={styles.icon}
            decoding="async"
            loading="lazy"
            src={iconUrl}
          />
        )}
        <Text as={level} className={styles.title} variant={myFreePack ? "subhead-2" : "subhead-1"} weight="medium">
          {titleImageUrl !== "" ?
            /* Named, not decorative: this image is the pack's title, so `alt` is the heading's only
               text. */
            <img
              alt={title}
              className={styles.titleImage}
              decoding="async"
              loading="lazy"
              src={titleImageUrl}
            /> :
            <>
              {baseTitle}
              {/* The suffix is drawn a step smaller than the name (Subhead 2
                  medium on `14694:34274`); the heading text is unchanged. */}
              {titleSuffix !== "" && (
                <Text as="span" variant="subhead-2" weight="medium">
                  {titleSuffix}
                </Text>
              )}
            </>}
        </Text>
        {pack.tooltip && (
          // GP3 — reuses atoms/tooltip whole: it already opens on hover,
          // tap and focus and dismisses on Escape AND page scroll (GP3's own
          // wording), so nothing here duplicates use-tooltip-dismiss.ts.
          <Tooltip className={styles.info} content={pack.tooltip} label={title} />
        )}
      </div>

      {subtitle !== "" && (
        <Text as="p" className={styles.subtitle} variant="body-2">
          {subtitle}
        </Text>
      )}

      {hasCta &&
        /* An add-on pack's CTA turns secondary below 735 (`14694:34829`, `14703:21994`). Button
           publishes no per-tier variant, so the two forms render and CSS shows one; the hidden one
           is `display: none`, out of the tab order and the accessibility tree. Latent until G6
           ships `kind: "addon"`; one Button with per-tier paint is the cleaner end state. */
        (pack.kind === "addon" ? (["tonal", "secondary"] as const) : (["tonal"] as const)).map(variant => (
          <Button
            // AX10 — an <a>, not a <button>: Button renders an anchor whenever `href` is set.
            ariaLabel={ctaAriaLabel(pack.ctaAriaLabel)}
            className={[styles.cta, pack.kind === "addon" ? CTA_TIER_CLASS[variant] : null]
              .filter(Boolean)
              .join(" ")}
            href={ctaHref}
            key={variant}
            linkAttributes
            linkPosition={LINK_POSITION_BODY}
            // 36px, 14px label: `14703:5134` (165x36) and mobile `14703:21899`.
            size="small"
            variant={variant}
          >
            {ctaLabel}
          </Button>
        ))}

      {/* Not a bare `&&`, for the reason `is-renderable.shared.ts` records: a `ReactNode` slot can be `false`, `""` or `0`, any of which would draw this wrapper's own spacing around nothing. */}
      {isRenderable(zipSlot) && <div className={styles.zip}>{zipSlot}</div>}

      {/* GP12 — the logo grids are the clamped region; the title row, the CTA
          and the footer link stay outside it, because the criterion clips
          "a pack whose LOGOS are taller than 475px". */}
      <PackCollapse
        className={styles.body}
        fits={packProvablyFits(subscriptionItems.length, channelItems.length)}
        toggleSlot={
          <Disclosure
            className={styles.showMore}
            hideChevron
            summary={<ShowMoreToggle variant="pill" lessLabel={showLessLabel} moreLabel={showMoreLabel} />}
          >
            {/* Nothing: this `<details>` is a switch, not a container. GP12 clips the pack with its first rows still visible, which a closed `<details>` cannot do — it hides its body outright (`disclosure.tsx`'s own note). */}
          </Disclosure>
        }
      >
        {/* GP5 / GP6 — the two rows are two different components in Figma, so each names its own: `variant` picks the tile, while the class carries this view's spacing through the hooks `logo-grid.module.css` publishes. */}
        <LogoGrid
          className={styles.subscriptions}
          heading={subsHeading}
          headingLevel={subLevel}
          items={subscriptionItems}
          nameFallback
          variant="genre-subscription"
        />
        <LogoGrid
          className={styles.channels}
          heading={chansHeading}
          headingLevel={subLevel}
          items={channelItems}
          variant={myFreePack ? "genre-myfree" : "genre-channel"}
        />
      </PackCollapse>

      {isRenderable(footerSlot) && <div className={styles.footer}>{footerSlot}</div>}
    </article>
  );
}
export const GenrePack = withSafeRender(GenrePackImpl, "GenrePack");

export default GenrePack;
